import axios from "axios";
import accessToken from "./jwt-token-access/accessToken";
import refreshToken from "./jwt-token-access/refreshToken";
import jwt_decode from "jwt-decode";

import * as url from "./url_helper";
import getOrgCode from "./jwt-token-access/getOrgCode";

const AWS_API_URL = process.env.REACT_APP_AWS_API_URL;

const axiosAwsApi = axios.create({
  baseURL: AWS_API_URL,
  headers: {
    "Content-Type": "application/json",
    //Added the following header
    "Access-Control-Allow-Origin": "*",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const refreshInstance = axios.create({
  baseURL: AWS_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/** Normalize API error body to a single display string (detail array, error, message, etc.). */
export function getApiErrorMessage(data) {
  if (!data) return "Request failed.";
  // When get() returns { status, data: { error, detail, ... } }, prefer inner body
  let inner =
    data.data !== undefined && data.data !== null ? data.data : data;
  if (typeof inner === "string" && inner.trim()) return inner.trim();
  if (typeof inner !== "object" || inner === null) return "Request failed.";
  const body = inner;
  if (typeof body.error === "string" && body.error.trim()) return body.error.trim();
  if (body.error && typeof body.error === "object") {
    const nested =
      typeof body.error.message === "string" ? body.error.message.trim() : "";
    if (nested) return nested;
  }
  if (typeof body.message === "string" && body.message.trim()) return body.message.trim();
  if (typeof body.errorMessage === "string" && body.errorMessage.trim())
    return body.errorMessage.trim();
  if (Array.isArray(body.detail) && body.detail.length > 0) {
    const first = body.detail[0];
    if (typeof first === "string") return first;
    if (first?.msg) return first.msg;
    if (first?.message) return first.message;
    return JSON.stringify(first);
  }
  if (typeof body.detail === "string" && body.detail.trim()) return body.detail.trim();
  return "Request failed.";
}

const handleGracefulError = (error) => {
  console.log("🚀 ~ handleGracefulError ~ error:", error)

  // Case 1: No response (CORS, network, timeout, etc.)
  if (!error || !error.response) {
    console.warn("Graceful handler triggered — no response or blocked by CORS");

    return {
      // Emulate a normal Axios error.response
      status: 500,
      data: {
        message: "An unexpected error occurred. Please try again later.",
      },
      // optional internal marker
      isSynthetic: true,
    };
  }

  // Case 2: Normal error response
  const status = error.response.status || 500;
  const data =
    error.response.data ||
    { message: "An unexpected server error occurred." };

  return {
    status,
    data,
  };
};


refreshInstance.interceptors.response.use(
  (response) => {
    const token = accessToken();
    processQueue(null, token);
    return response;
  },
  (error) => {
    processQueue(error, null);
    return Promise.reject(error);
  }
);

axiosAwsApi.interceptors.request.use(
  async (config) => {
    console.log("AWS API Request Interceptor, Request intercept :: ", config);

    const token = accessToken();
    console.log("Adding token :: ", token);

    const orgCode = getOrgCode();

    let authUser = null;
    let username = null;
    if (token) {
      authUser = jwt_decode(token);
    }
    if (authUser) {
      username = authUser.sub.charAt(0) + authUser.sub.slice(1);

      console.log("aws_api_helper, Adding username :: ", username);
    }

    config.headers["Authorization"] = token ? `Bearer ${token}` : "";
    config.headers["username"] = username;
    config.headers["X-Cloud-Catcher-Scope"] = orgCode;

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"]; // Let Axios set it automatically
    } else {
      config.headers["Content-Type"] = "application/json"; // Set default for JSON
    }

    return config;
  },
  (error) => {
    console.log("AWS API Request Interceptor, error :: ", error);
    return Promise.reject(error);
  }
);

axiosAwsApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.log("AWS API Response Interceptor during error, error :: ", error);
    try {
      const status = error.response?.status;
      const originalRequest = error.config;
      const refToken = refreshToken();
      const accToken = accessToken();

      let authUser = null;
      let username = null;
      if (accToken) {
        authUser = jwt_decode(accToken);
      }
      if (authUser) {
        username = authUser.sub.charAt(0) + authUser.sub.slice(1);
      }

      if (
        status === 401 &&
        !originalRequest._retry &&
        refToken &&
        refToken !== ""
      ) {
        console.log(">AWS API Response Interceptor, status = ", status);
        if (isRefreshing) {
          console.log(">AWS API Response Interceptor, isRefreshing ");
          try {
            const token = await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });

            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            console.log(
              ">AWS API Response Interceptor, Returning request with token "
            );
            return axiosAwsApi(originalRequest);
          } catch (error) {
            console.log(">AWS API Response Interceptor, isRefreshing > catch ");
            return Promise.reject(error);
          }
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          console.log(
            ">AWS API Response Interceptor, getting a new refresh token "
          );
          const response = await refreshInstance.post(url.POST_LAMBDA_REFRESH, {
            refreshToken: refToken,
            username: username,
          });

          let authUser = response.data;
          authUser.token = authUser.accessToken;
          localStorage.setItem("authUser", JSON.stringify(authUser));

          console.log(">AWS API Response Interceptor, refresh token retrieved");
          const token = accessToken();
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          console.log(
            ">AWS API Response Interceptor, Returning original request with new token"
          );
          return axiosAwsApi(originalRequest);
        } catch (error) {
          console.log(
            ">AWS API Response Interceptor, try, catch, error = ",
            error
          );
          processQueue(error, null);
          window.location.href = "/sessionExpired";
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    } catch (error) {
      console.log(">AWS API Response Interceptor, catch, error = ", error);
      return Promise.reject(error);
    }
  }
);

export async function get(url, config = {}) {
  return axiosAwsApi
    .get(url, { ...config })
    .then((response) => ({ ...response.data, status: response.status }))
    .catch((err) => {
      return handleGracefulError(err);
    });
}

export async function getBodyOnly(url, config = {}) {
  return axiosAwsApi
    .get(url, { ...config })
    .then((response) => ({ ...response.data }))
    .catch((err) => {
      return handleGracefulError(err);
    });
}

export async function post(url, data, config = {}) {
  console.log("Post AWS called with config: ", config);
  return axiosAwsApi
    .post(url, data, { ...config })
    .then((response) => response)
    .catch((err) => {
      return handleGracefulError(err);
    });
  // .catch((error) => console.log("In post call :: ", error.response.status, "Error :: ", error.response.data ));
}

export async function put(url, data, config = {}) {
  console.log("Put AWS called with data: ", data, "url: ", url);
  return axiosAwsApi
    .put(url, { ...data }, { ...config })
    .then((response) => ({ ...response.data, status: response.status }))
    .catch((err) => {
      return handleGracefulError(err);
    });
}

export async function del(url, config = {}) {
  console.log("Delete AWS called with url: ", url);
  return axiosAwsApi
    .delete(url, { ...config })
    .then((response) => {
      const data =
        typeof response.data === "string"
          ? response.data
          : JSON.stringify(response.data);
      return { data, status: response.status };
    })
    .catch((err) => {
      return handleGracefulError(err);
    });
}

export default axiosAwsApi;
